/*
 * Copyright (c) Nexign, JSC, 2021-2025.
 */
/**
 * ProductCard Campaign Tariff
 *
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.39.0
 * @since 1.11.0
 */

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { ImgStyled } from '@components/common/Block/Img';
import { makeSelectTranslationPageProducts } from '@modules/internationalization/intSelectors';
import { IPageProductsTranslation } from '@modules/internationalization/types/pages/products';
import { ProductCardInternetLimited } from '@pages/PageProducts/components/ProductCards/ProductCardInternetLimited';
import {
    getRoamingTooltip,
    PRODUCT_CARD_CAMPAIGN_TARIFF_UNLIMITED_VALUE,
} from '@pages/PageProducts/components/ProductCards/utils/rateplanCardUtils';
import { IRootReducer } from '@reducers/rootReducer';
import { stringFormatParams } from '@utils/stringFormat';

import { icons } from '../../../../assets';
import { EllipseText } from '../../../../components/common';
import ArrowTooltip from '../../../../components/common/Tooltip/Tooltip';
import {
    EProductCardTariffRoamingType,
    IProductCardTariff,
    IProductCardTariffFieldRoaming,
} from '../../tariffsAndServicesProductTariffTypes';
import {
    ProductCardCampaignTariffStyled,
    ProductCardPersonalInfoStyled,
    ProductCardTariffFieldWrapperStyled,
    ProductCardTariffTitleStyled,
} from './styled/ProductCardTariffStyled';
import ProductCardTariffFieldRoaming from './Tariff/ProductCardTariffFieldRoaming';
import ProductCardTariffPrice from './Tariff/ProductCardTariffPrice';

export type TProductCardCampaignTariff = IProductCardTariff & IConnectedProps;

export const ProductCardCampaignTariff = (props: TProductCardCampaignTariff) => {
    const {
        connected,
        limitConnections,
        internetLimited,
        payment,
        productTranslation,
        roamings,
        title,
    } = props;

    const cardTranslation = productTranslation.campaignTariffsCard;

    const getRoamingIcon = (roaming: IProductCardTariffFieldRoaming): string | undefined => roaming.type
        ? {
            [EProductCardTariffRoamingType.Calls]: icons.callsGreenIcon,
            [EProductCardTariffRoamingType.Internet]: icons.roamingGreenIcon,
            [EProductCardTariffRoamingType.InternetLimited]: icons.roamingGreenIcon,
            [EProductCardTariffRoamingType.SMS]: icons.mailGreenIcon,
        }[roaming.type]
        : undefined;

    const limitConnectionsTitle = stringFormatParams(productTranslation.limitConnections, {
        limitConnections: limitConnections,
        connected: connected,
    });

    return (
        <ProductCardCampaignTariffStyled height="auto">
            <ProductCardTariffTitleStyled>
                <ProductCardPersonalInfoStyled>
                    <EllipseText lines={3} title={title}>
                        {title}
                    </EllipseText>
                    {!!limitConnections && (<ArrowTooltip limit-connections-toolip='limitConnectionsTooltip' title={limitConnectionsTitle}>
                        <ImgStyled className="small" src={icons.infoSuccessSmallIcon} width={'20px'}/>
                    </ArrowTooltip>)}
                </ProductCardPersonalInfoStyled>
            </ProductCardTariffTitleStyled>
            <ProductCardTariffFieldWrapperStyled>
                {
                    //  раздел "Базовый состав пакета"
                    roamings?.value.map((roaming: IProductCardTariffFieldRoaming) => (
                        <ProductCardTariffFieldRoaming
                            key={roaming.value}
                            svgIconUrl={getRoamingIcon(roaming)}
                            tooltip={getRoamingTooltip(roaming, cardTranslation)}
                            value={+roaming.value === PRODUCT_CARD_CAMPAIGN_TARIFF_UNLIMITED_VALUE
                                ? cardTranslation.roamingUnlimited
                                : roaming.value}
                        />
                    ))
                }
                <ProductCardInternetLimited
                    cardTranslation={cardTranslation}
                    internetLimited={internetLimited}
                />
            </ProductCardTariffFieldWrapperStyled>
            {
                // Абонентская плата
                payment && <ProductCardTariffPrice
                    unit={payment.value.unit ?? ''}
                    value={payment.value.value}
                />
            }
        </ProductCardCampaignTariffStyled>
    );
};

interface IConnectedProps {
    productTranslation: IPageProductsTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    productTranslation: makeSelectTranslationPageProducts(),
});

export default connect(mapStateToProps)(ProductCardCampaignTariff);
